import { LABELS } from '@constants'
import { formatColumnData } from '@helpers/formatColumnData.helper'
import { useIsMobile } from '@hooks/useIsMobile'
import { Box, LinearProgress } from '@mui/material'
import {
  DataGrid,
  DataGridProps,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { DatagridStore } from '@stores/datagrid.stores'
import { IDatagridResponsiveColumn } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { DATA_GRID } from 'src/constants/components.constants'

interface IOvDatagridProps extends Partial<DataGridProps> {
  store: DatagridStore
  params?: Record<string, unknown>
  customColumns?: IDatagridResponsiveColumn
  withToolBar?: boolean
  responsiveColumns?: boolean
  hidePagination?: boolean
}

export const OvDataGrid = observer(
  ({
    store,
    onRowClick,
    params,
    customColumns,
    withToolBar = false,
    hidePagination = false,
    slots,
    ...rest
  }: IOvDatagridProps) => {
    const isMobile = useIsMobile()

    const _columns = useMemo(() => formatColumnData(store.columns), [store.columns])

    const CustomToolbar = () => {
      return (
        <GridToolbarContainer
          sx={({ palette }) => ({
            p: 2,
            borderBottom: '2px solid',
            borderBottomColor: palette.divider,
            gap: isMobile ? 0 : 2,
          })}>
          <GridToolbarQuickFilter
            variant="outlined"
            size="small"
            placeholder={LABELS.SEARCH}
            sx={{
              flex: isMobile ? '1 0 100%' : 'initial',
              mb: isMobile ? 0 : 'initial',
            }}
          />
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <GridToolbarColumnsButton />
              <GridToolbarFilterButton />
              <GridToolbarDensitySelector />
            </>
          )}
        </GridToolbarContainer>
      )
    }

    useEffect(() => {
      store.getData(params, isMobile, customColumns)

      return () => store.reset()
    }, [params, isMobile, customColumns])

    return (
      <>
        {/* <Box
          sx={{
            marginBottom: 2,
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
          <Collapse in={!removeDisabled}>
            <ButtonGroup size="small">
              <Button
                onClick={handleRemoveSelection}
                variant="contained"
                color="secondary"
                disabled={removeDisabled}
                endIcon={<Delete />}>
                Verwijder selectie
              </Button>
            </ButtonGroup>
          </Collapse>
        </Box> */}
        <DataGrid
          {...DATA_GRID}
          loading={store.is_loading}
          columns={_columns}
          rowCount={store.current_pagination?.total}
          rows={store.current_rows}
          disableRowSelectionOnClick
          onRowClick={onRowClick}
          disableColumnMenu
          autoHeight={!isMobile}
          hideFooter={hidePagination}
          localeText={{
            // Pagination
            MuiTablePagination: {
              labelRowsPerPage: 'Rijen per pagina:',
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}–${to} van ${count !== -1 ? count : `meer dan ${to}`}`,
            },
            // Footer
            footerRowSelected: (count) =>
              count !== 1
                ? `${count.toLocaleString()} rijen geselecteerd`
                : `${count.toLocaleString()} rij geselecteerd`,
            // Toolbar
            toolbarDensity: 'Rijhoogte',
            toolbarDensityLabel: 'Rijhoogte',
            toolbarDensityCompact: 'Compact',
            toolbarDensityStandard: 'Standaard',
            toolbarDensityComfortable: 'Comfortabel',
          }}
          slots={{
            loadingOverlay: LinearProgress,
            noRowsOverlay: () => (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                Geen gegevens
              </Box>
            ),
            toolbar: withToolBar ? CustomToolbar : null,
            ...slots,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          sx={({
            palette: {
              primary: { main },
              background: { paper },
            },
          }) => ({
            background: paper,
            '.MuiDataGrid-columnHeaderTitle': {
              fontWeight: '700',
              color: main,
            },
            '.MuiTablePagination-displayedRows': {
              fontWeight: '700',
              color: main,
            },
            '.MuiDataGrid-overlayWrapperInner': {
              background: paper,
            },
            '.MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '.MuiDataGrid-columnHeaders': {
              display: isMobile ? 'none' : 'initial',
            },
            '.MuiDataGrid-virtualScrollerRenderZone': {
              width: isMobile ? '100%' : 'initial',
            },
          })}
          paginationMode="client"
          {...rest}
        />
      </>
    )
  }
)
